(() => {
  const VALA = window.VALA || {};
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  VALA.focusable = (container) =>
    Array.from(container.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hasAttribute('hidden') && element.offsetParent !== null
    );

  VALA.trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const elements = VALA.focusable(container);
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  VALA.announce = (message) => {
    const region = document.getElementById('GlobalLiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  };

  window.VALA = VALA;

  class MobileMenu {
    constructor() {
      this.dialog = document.getElementById('MobileMenu');
      this.trigger = document.querySelector('[data-mobile-menu-open]');
      if (!this.dialog || !this.trigger) return;
      this.boundKeydown = this.onKeydown.bind(this);
      this.trigger.addEventListener('click', () => this.open());
      this.dialog.querySelectorAll('[data-mobile-menu-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
    }

    open() {
      this.lastFocus = document.activeElement;
      this.dialog.hidden = false;
      document.body.classList.add('is-locked');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.boundKeydown);
      const focusable = VALA.focusable(this.dialog);
      if (focusable.length) focusable[0].focus();
    }

    close() {
      this.dialog.hidden = true;
      document.body.classList.remove('is-locked');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.boundKeydown);
      if (this.lastFocus) this.lastFocus.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      VALA.trapFocus(this.dialog, event);
    }
  }

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-target-media]'));
      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => this.select(thumbnail.dataset.targetMedia));
      });
      this.querySelectorAll('.product-media__zoom').forEach((button) => {
        button.addEventListener('click', () => this.openZoom(button.querySelector('img')));
      });
      const stage = this.querySelector('.product-media__stage');
      if (stage) {
        let touchStart = null;
        stage.addEventListener('touchstart', (event) => {
          touchStart = event.changedTouches[0].clientX;
        }, { passive: true });
        stage.addEventListener('touchend', (event) => {
          if (touchStart === null) return;
          const delta = event.changedTouches[0].clientX - touchStart;
          if (Math.abs(delta) > 45) this.step(delta < 0 ? 1 : -1);
          touchStart = null;
        }, { passive: true });
      }
    }

    select(mediaId) {
      if (!mediaId) return;
      this.items.forEach((item) => {
        const active = String(item.dataset.mediaId) === String(mediaId);
        item.hidden = !active;
        item.classList.toggle('is-active', active);
      });
      this.thumbnails.forEach((thumbnail) => {
        const active = String(thumbnail.dataset.targetMedia) === String(mediaId);
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }

    step(direction) {
      if (this.items.length < 2) return;
      const currentIndex = this.items.findIndex((item) => !item.hidden);
      const nextIndex = (currentIndex + direction + this.items.length) % this.items.length;
      this.select(this.items[nextIndex].dataset.mediaId);
    }

    openZoom(image) {
      if (!image) return;
      const dialog = document.createElement('dialog');
      dialog.className = 'media-zoom-dialog';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'icon-button media-zoom-dialog__close';
      close.setAttribute('aria-label', document.documentElement.lang === 'de' ? 'Schließen' : 'Close');
      close.textContent = '×';
      const zoomed = document.createElement('img');
      zoomed.src = image.src || image.currentSrc;
      zoomed.alt = image.alt || '';
      dialog.append(close, zoomed);
      document.body.append(dialog);
      close.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => dialog.remove(), { once: true });
      dialog.showModal();
      close.focus();
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded === 'true') return;
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.load();
      }, { rootMargin: '300px 0px' });
      observer.observe(this);
    }

    async load() {
      this.dataset.loaded = 'true';
      try {
        const response = await fetch(this.dataset.url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(String(response.status));
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const source = html.querySelector('product-recommendations');
        if (source && source.innerHTML.trim()) this.innerHTML = source.innerHTML;
      } catch (error) {
        this.hidden = true;
      }
    }
  }

  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const current = this.dataset.currentHandle;
      if (!current) return;
      const key = 'valaRecentlyViewed';
      let handles = [];
      try {
        handles = JSON.parse(sessionStorage.getItem(key) || '[]');
      } catch (error) {
        handles = [];
      }
      handles = handles.filter((handle) => handle && handle !== current);
      const displayHandles = handles.slice(0, Number(this.dataset.limit || 4));
      try {
        sessionStorage.setItem(key, JSON.stringify([current, ...handles].slice(0, 12)));
      } catch (error) {
        return;
      }
      if (!displayHandles.length) return;
      const products = await Promise.all(displayHandles.map((handle) => this.fetchProduct(handle)));
      products.filter(Boolean).forEach((product) => this.renderProduct(product));
      if (this.querySelector('[data-recently-viewed-grid]')?.children.length) this.hidden = false;
    }

    async fetchProduct(handle) {
      try {
        const root = VALA.routes.root.endsWith('/') ? VALA.routes.root : `${VALA.routes.root}/`;
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    renderProduct(product) {
      const grid = this.querySelector('[data-recently-viewed-grid]');
      if (!grid) return;
      const card = document.createElement('article');
      card.className = 'product-card';
      const media = document.createElement('a');
      media.className = 'product-card__media product-card__media--square';
      media.href = product.url;
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = product.featured_image;
        image.alt = product.title;
        image.loading = 'lazy';
        image.width = 600;
        image.height = 600;
        media.append(image);
      }
      const content = document.createElement('div');
      content.className = 'product-card__content';
      const heading = document.createElement('h3');
      heading.className = 'product-card__title';
      const link = document.createElement('a');
      link.href = product.url;
      link.textContent = product.title;
      heading.append(link);
      const price = document.createElement('div');
      price.className = 'price';
      price.textContent = this.formatMoney(product.price);
      const button = document.createElement('a');
      button.className = 'button button--secondary button--full';
      button.href = product.url;
      button.textContent = VALA.strings.viewProduct || 'Produkt ansehen';
      content.append(heading, price, button);
      card.append(media, content);
      grid.append(card);
    }

    formatMoney(cents) {
      const amount = (Number(cents || 0) / 100).toLocaleString(document.documentElement.lang || 'de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return (VALA.moneyFormat || '{{amount}}').replace(/\{\{\s*amount(?:_with_comma_separator)?\s*\}\}/, amount);
    }
  }

  const initializeAccordions = () => {
    document.querySelectorAll('details > summary').forEach((summary) => {
      const details = summary.parentElement;
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      });
    });
  };

  const initializeQuantityInputs = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.quantity__button');
      if (!button) return;
      const quantity = button.closest('.quantity');
      const input = quantity?.querySelector('.quantity__input');
      if (!input) return;
      const previous = Number(input.value || 0);
      const step = Number(input.step || 1);
      const minimum = Number(input.min || 0);
      const next = button.name === 'plus' ? previous + step : Math.max(minimum, previous - step);
      if (next === previous) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

  };

  const initializeLocalization = () => {
    document.querySelectorAll('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => select.form?.submit());
    });
  };

  const initializeSorting = () => {
    document.querySelectorAll('[data-sort-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url.toString());
      });
    });
  };

  const initializeFacets = () => {
    const panel = document.querySelector('[data-facets-form]');
    const open = document.querySelector('[data-facets-open]');
    const close = document.querySelector('[data-facets-close]');
    if (!panel || !open) return;
    panel.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement) || !['checkbox', 'number'].includes(event.target.type)) return;
      panel.requestSubmit();
    });
    let priceFilterTimer;
    panel.addEventListener('input', (event) => {
      if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'number') return;
      clearTimeout(priceFilterTimer);
      priceFilterTimer = setTimeout(() => panel.requestSubmit(), 500);
    });

    const setOpen = (isOpen) => {
      panel.classList.toggle('is-open', isOpen);
      open.setAttribute('aria-expanded', String(isOpen));
      document.body.classList.toggle('is-locked', isOpen);
      if (isOpen) close?.focus();
      else open.focus();
    };
    open.addEventListener('click', () => setOpen(true));
    close?.addEventListener('click', () => setOpen(false));
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
      else if (panel.classList.contains('is-open')) VALA.trapFocus(panel, event);
    });
  };

  const initializeArticleToc = () => {
    const content = document.querySelector('[data-article-content]');
    const toc = document.querySelector('[data-article-toc]');
    const list = toc?.querySelector('[data-article-toc-list]');
    if (!content || !toc || !list) return;
    const headings = Array.from(content.querySelectorAll('h2, h3'));
    if (headings.length < 2) return;
    headings.forEach((heading, index) => {
      if (!heading.id) heading.id = `abschnitt-${index + 1}`;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      if (heading.tagName === 'H3') item.className = 'article-toc__subitem';
      item.append(link);
      list.append(item);
    });
    toc.hidden = false;
  };

  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
  if (!customElements.get('recently-viewed')) customElements.define('recently-viewed', RecentlyViewed);

  document.addEventListener('DOMContentLoaded', () => {
    new MobileMenu();
    initializeAccordions();
    initializeQuantityInputs();
    initializeLocalization();
    initializeSorting();
    initializeFacets();
    initializeArticleToc();
  });
})();
